// src/components/analytics/AnalyticsWidgets.jsx
import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

function AnalyticsLoading() {
  return <div className="card-loading">Loading analytics…</div>;
}

export function MetricCard({ title, value, subtitle, loading }) {
  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">{title}</h3>
      </div>
      <div className="summary-card-body">
        <span className="metric-value">{loading ? "—" : value}</span>
        {subtitle && <p className="metric-subtext">{subtitle}</p>}
      </div>
    </div>
  );
}

export function WeeklyAttendanceChart({ data, loading }) {
  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Weekly Attendance</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={data}
            margin={{ top: 10, right: 14, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="day" tickLine={false} axisLine={false} />
            <YAxis tickLine={false} axisLine={false} />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="present"
              stackId="a"
              fill="#3B82F6"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="remote"
              stackId="a"
              fill="#34D399"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="leave"
              stackId="a"
              fill="#FBBF24"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="absent"
              stackId="a"
              fill="#F87171"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function TeamStatusSummary({ counts, loading }) {
  const stats = [
    { key: "present", label: "Present", color: "#3B82F6" },
    { key: "remote", label: "Remote", color: "#34D399" },
    { key: "leave", label: "On Leave", color: "#FBBF24" },
    { key: "absent", label: "Absent", color: "#F87171" },
  ];

  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Team Status Overview</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <div className="status-summary-grid">
          {stats.map((stat) => (
            <div key={stat.key} className="status-summary-item">
              <span
                className="status-summary-value"
                style={{ color: stat.color }}
              >
                {counts[stat.key] ?? 0}
              </span>
              <span className="status-summary-label">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DepartmentBreakdownChart({ data, loading }) {
  // Dynamically calculate chart height to accommodate all departments without scrolling issues
  const chartHeight = Math.max(180, data.length * 30); // 30px per department row
  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Department Breakdown</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <>
          {/* Container with dynamic height */}
          <div style={{ width: "100%", height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                // Increase left margin for long department names and ensure all labels are shown
                margin={{ top: 10, right: 16, left: 80, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="department"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  // Force every label to render without skipping
                  interval={0}
                  // Allocate enough width for the longest label
                  width={150}
                />
                <Tooltip />
                <Bar
                  dataKey="total"
                  fill="#3B82F6"
                  radius={[0, 6, 6, 0]}
                  barSize={16}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="analytics-dept-footer">
            {data.map((dept) => (
              <div key={dept.department} className="analytics-dept-item">
                <span>{dept.department}</span>
                <span>{dept.presentRate}% present</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ShiftDistributionChart({ data, loading }) {
  const COLORS = ["#1b4b31", "#3B82F6", "#FBBF24", "#F87171"];

  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Shift Distribution</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend verticalAlign="bottom" height={36} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function AttendanceTrendChart({ data, loading }) {
  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Attendance Trend</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1b4b31" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#1b4b31" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
            <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="present"
              stroke="#1b4b31"
              fillOpacity={1}
              fill="url(#colorPresent)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function WorkforceBreakdown({ stats, loading }) {
  const items = [
    { label: "Total Workforce", value: stats.total, color: "var(--brand-accent)" },
    { label: "Active Now", value: stats.active, color: "var(--green)" },
    { label: "On Leave", value: stats.leave, color: "var(--amber)" },
    { label: "Remote", value: stats.remote, color: "var(--blue)" },
  ];

  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Workforce Overview</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <div className="workforce-grid">
          {items.map((item) => (
            <div key={item.label} className="workforce-item">
              <span className="workforce-label">{item.label}</span>
              <span className="workforce-value" style={{ color: item.color }}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function LeaveSummaryWidget({ data, loading }) {
  return (
    <div className="dashboard-card analytics-card">
      <div className="card-header">
        <h3 className="card-title analytics-title">Leave Distribution</h3>
      </div>
      {loading ? (
        <AnalyticsLoading />
      ) : (
        <div className="leave-summary-list">
          {data.map((item) => (
            <div key={item.type} className="leave-summary-row">
              <div className="leave-summary-info">
                <span className="leave-summary-type">{item.type}</span>
                <span className="leave-summary-count">{item.count} requests</span>
              </div>
              <div className="leave-summary-bar-wrap">
                <div
                  className="leave-summary-bar"
                  style={{
                    width: `${item.percentage}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
